import { Dispatch, SetStateAction, useState } from 'react';
import { QueryKey, UseQueryResult, useQuery, useQueryClient } from 'react-query';

import { GetTeamMembers } from '@pimm/services/lib/sms-workforce/services';
import { GetTeamMembersQueryParams } from '@pimm/services/lib/sms-workforce/types';
import { SyncInfoDto, TeamMember } from '../types';

export type GetTeamMembersReturn = {
  teamMembers: TeamMember[];
  syncInfo?: SyncInfoDto;
};

export const useGetTeamMembers = (props?: {
  queryKey?: string;
  query?: GetTeamMembersQueryParams;
}): [
  UseQueryResult<GetTeamMembersReturn>,
  Dispatch<SetStateAction<GetTeamMembersQueryParams>>,
  (next: TeamMember) => void,
  (created: TeamMember) => void,
] => {
  const [query, setQuery] = useState<GetTeamMembersQueryParams>(props?.query ?? {});
  const queryClient = useQueryClient();
  const queryKey = [props?.queryKey ?? 'GetTeamMembers', query] as [QueryKey, GetTeamMembersQueryParams];

  const result = useQuery({
    enabled: !!query.siteId,
    queryKey,
    queryFn: async ({ queryKey }): Promise<GetTeamMembersReturn> => {
      const response = await GetTeamMembers(queryKey[1]);
      // Trust the merged-endpoint contract (Employee record + nested login-health fields) —
      // same trust boundary as every other `@pimm/services` consumer in this codebase.
      return { teamMembers: (response.teamMembers ?? []) as TeamMember[], syncInfo: response.syncInfo };
    },
  });

  // Patches one member into the existing cache — every write endpoint's response
  // contract guarantees the full refreshed record, so this is a whole-record
  // replace, never a follow-up GET.
  const updateCachedMember = (next: TeamMember) => {
    queryClient.setQueryData<GetTeamMembersReturn | undefined>(queryKey, old =>
      old
        ? {
            ...old,
            teamMembers: old.teamMembers.map(m => {
              if (m.id === next.id) return next;
              // SetPrimary's response only reflects the new primary — the
              // previous primary's flip-off isn't in this record, so clear it
              // locally. Single-primary-per-site invariant makes this a no-op
              // for every other write path.
              if (next.user?.isPrimary && m.user?.isPrimary) {
                return { ...m, user: { ...m.user, isPrimary: false } };
              }
              return m;
            }),
          }
        : old,
    );
  };

  // Add Employee's create response is a complete, enriched row (same shape as
  // a GET TeamMembers row) and site-level syncInfo is unchanged by an add, so
  // a local prepend is fully consistent — no invalidate/refetch.
  const insertCachedMember = (created: TeamMember) => {
    queryClient.setQueryData<GetTeamMembersReturn | undefined>(queryKey, old =>
      old ? { ...old, teamMembers: [created, ...old.teamMembers] } : old,
    );
  };

  return [result, setQuery, updateCachedMember, insertCachedMember];
};
